// Fuzzy matching helpers: Jaro-Winkler + token-set ratio
import { normalizeText, normalizeTokens, normalizeEenheid } from './normalizer.js';

export function jaro(s1: string, s2: string): number {
  if (s1 === s2) return 1;
  if (!s1 || !s2) return 0;
  const matchDist = Math.floor(Math.max(s1.length, s2.length) / 2) - 1;
  const m1 = new Array(s1.length).fill(false);
  const m2 = new Array(s2.length).fill(false);
  let matches = 0;
  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchDist);
    const end = Math.min(s2.length, i + matchDist + 1);
    for (let j = start; j < end; j++) {
      if (m2[j]) continue;
      if (s1[i] !== s2[j]) continue;
      m1[i] = true;
      m2[j] = true;
      matches++;
      break;
    }
  }
  if (!matches) return 0;
  let t = 0;
  let k = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!m1[i]) continue;
    while (!m2[k]) k++;
    if (s1[i] !== s2[k]) t++;
    k++;
  }
  t = t / 2;
  return (matches / s1.length + matches / s2.length + (matches - t) / matches) / 3;
}

export function jaroWinkler(s1: string, s2: string, p: number = 0.1): number {
  const j = jaro(s1, s2);
  let l = 0;
  while (l < 4 && l < Math.min(s1.length, s2.length) && s1[l] === s2[l]) l++;
  return j + l * p * (1 - j);
}

export function tokenSetRatio(s1: string, s2: string): number {
  const a = new Set(normalizeTokens(s1));
  const b = new Set(normalizeTokens(s2));
  if (!a.size && !b.size) return 1;
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const union = a.size + b.size - inter;
  return inter / union;
}

export function combinedScore(
  pdfWerken: string,
  plWerken: string,
  pdfEenheid: string,
  plEenheid: string,
): number {
  const a = normalizeText(pdfWerken);
  const b = normalizeText(plWerken);
  const jw = jaroWinkler(a, b);
  const ts = tokenSetRatio(pdfWerken, plWerken);
  const ue = normalizeEenheid(pdfEenheid) === normalizeEenheid(plEenheid) ? 1 : 0;
  const eUsable = pdfEenheid && plEenheid;
  return 0.6 * jw + 0.3 * ts + (eUsable ? 0.1 * ue : 0.05);
}

export type ScoredItem<T> = { item: T; score: number };

export function findTopMatches<T extends { werken?: string; pdfNaam?: string; eenheid: string }>(
  parsedItem: { werken: string; eenheid: string },
  items: T[],
  topN: number = 3,
): ScoredItem<T>[] {
  const scored = items.map((it) => ({
    item: it,
    score: combinedScore(parsedItem.werken, it.werken ?? it.pdfNaam ?? '', parsedItem.eenheid, it.eenheid),
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, topN);
}
